import { targetHasScroll } from './layout';

interface IScrollPosition {
  x: boolean;
  y: boolean;
}

export interface BodyScrollOptions {
  reserveScrollBarGap?: boolean;
  allowTouchMove?: (el: HTMLElement, scrollPosition: IScrollPosition) => boolean;
}

interface Lock {
  targetElement: HTMLElement;
  options: BodyScrollOptions;
}

// Older browsers don't support event options, feature detect it.
let hasPassiveEvents = false;
if (typeof window !== 'undefined') {
  const passiveTestOptions = {
    get passive() {
      hasPassiveEvents = true;
      return undefined;
    },
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: Unreachable code error
  window.addEventListener('testPassive', null, passiveTestOptions);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: Unreachable code error
  window.removeEventListener('testPassive', null, passiveTestOptions);
}

type HandleScrollEvent = TouchEvent;

let locks: Array<Lock> = [];
let documentListenerAdded = false;
let initialClientY = -1;
let initialClientX = -1;
let previousBodyOverflowSetting: string | undefined;
let previousBodyOverscrollBehaviorSetting: string | undefined;
let previousBodyPosition:
  | { position: string; top: string; left: string; right: string }
  | undefined;

// returns true if `el` should be allowed to receive touchmove events.
const allowTouchMove = (el: EventTarget, scrollPosition: IScrollPosition): boolean =>
  locks.some((lock) => {
    if (
      lock.options.allowTouchMove &&
      lock.options.allowTouchMove(el as HTMLElement, scrollPosition)
    ) {
      return true;
    }

    return false;
  });

const preventDefault = (rawEvent: HandleScrollEvent): boolean => {
  const e = rawEvent || window.event;

  // For the case whereby consumers adds a touchmove event listener to document.
  // Recall that we do document.addEventListener('touchmove', preventDefault, { passive: false })
  // in disableBodyScroll - so if we provide this opportunity to allowTouchMove, then
  // the touchmove event on document will break.
  if (e.target && allowTouchMove(e.target, { x: false, y: false })) {
    return true;
  }

  if (e.touches.length > 1) return true;

  if (e.preventDefault) e.preventDefault();

  return false;
};

const setPositionFixed = () =>
  window.requestAnimationFrame(() => {
    // If previousBodyPosition is already set, don't set it again.
    if (previousBodyPosition === undefined) {
      previousBodyPosition = {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
      };

      // Update the dom inside an animation frame
      const { scrollY, scrollX, innerHeight } = window;
      if (targetHasScroll(document.body)) {
        document.body.style.overflowY = 'scroll';
      }
      document.body.style.position = 'fixed';
      document.body.style.top = `${-scrollY}px`;
      document.body.style.left = `${-scrollX}px`;
      document.body.style.right = '0px';

      // disable pull-to-refresh in ios
      // If previousBodyOverflowSetting is already set, don't set it again.
      if (previousBodyOverflowSetting === undefined) {
        previousBodyOverflowSetting = document.body.style.overflow;
        // document.body.style.overflow = 'hidden';
      }

      if (previousBodyOverscrollBehaviorSetting === undefined) {
        previousBodyOverscrollBehaviorSetting = document.body.style.overscrollBehavior;

        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';
      }

      setTimeout(
        () =>
          window.requestAnimationFrame(() => {
            // Attempt to check if the bottom bar appeared due to the position change
            const bottomBarHeight = innerHeight - window.innerHeight;
            if (bottomBarHeight && scrollY >= innerHeight) {
              // Move the content further up so that the bottom bar doesn't hide it
              document.body.style.top = `${(scrollY + bottomBarHeight) * -1}`;
            }
          }),
        300
      );
    }
  });

const restorePositionSetting = () => {
  if (previousBodyPosition !== undefined) {
    // Convert the position from "px" to Int
    const y = -parseInt(document.body.style.top, 10);
    const x = -parseInt(document.body.style.left, 10);

    // Restore styles
    document.body.style.position = previousBodyPosition.position;
    document.body.style.top = previousBodyPosition.top;
    document.body.style.left = previousBodyPosition.left;
    document.body.style.right = previousBodyPosition.right;

    // enable pull-to-refresh in ios
    if (previousBodyOverflowSetting !== undefined) {
      document.body.style.overflow = previousBodyOverflowSetting;

      // Restore previousBodyOverflowSetting to undefined
      // so setOverflowHidden knows it can be set again.
      previousBodyOverflowSetting = undefined;
    }

    if (previousBodyOverscrollBehaviorSetting !== undefined) {
      document.body.style.overscrollBehavior = previousBodyOverscrollBehaviorSetting;
      document.documentElement.style.overscrollBehavior = previousBodyOverscrollBehaviorSetting;

      // Restore previousBodyOverscrollBehaviorSetting to undefined
      // so setOverflowHidden knows it can be set again.
      previousBodyOverscrollBehaviorSetting = undefined;
    }

    // Restore scroll
    window.scrollTo(x, y);

    previousBodyPosition = undefined;
  }
};

// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
const isTargetElementTotallyScrolled = (targetElement: HTMLElement): boolean =>
  targetElement
    ? targetElement.scrollHeight - targetElement.scrollTop <= targetElement.clientHeight
    : false;

const checkScrollXY = (x: number, y: number) => {
  const position = {
    x: false,
    y: false,
  };

  if (!initialClientX || !initialClientY) {
    return position;
  }

  const xDiff = initialClientY - x;
  const yDiff = initialClientX - y;

  if (Math.abs(xDiff) > Math.abs(yDiff)) {
    position.x = true;
  } else {
    position.y = true;
  }

  return position;
};

const handleScroll = (event: HandleScrollEvent, targetElement: HTMLElement): boolean => {
  const clientY = event.targetTouches[0].clientY - initialClientY;

  const scrollPosition = checkScrollXY(
    event.targetTouches[0].clientX,
    event.targetTouches[0].clientY
  );

  if (event.target && allowTouchMove(event.target, scrollPosition)) {
    return false;
  }

  if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
    // element is at the top of its scroll.
    return preventDefault(event);
  }

  if (isTargetElementTotallyScrolled(targetElement) && clientY < 0) {
    // element is at the bottom of its scroll.
    return preventDefault(event);
  }

  event.stopPropagation();
  return true;
};

export const disableBodyScroll = (
  targetElement: HTMLElement,
  options?: BodyScrollOptions
): void => {
  // targetElement must be provided
  if (!targetElement) {
    // eslint-disable-next-line no-console
    console.error(
      'disableBodyScroll unsuccessful - targetElement must be provided when calling disableBodyScroll on IOS devices.'
    );
    return;
  }

  // disableBodyScroll must not have been called on this targetElement before
  if (locks.some((lock) => lock.targetElement === targetElement)) {
    return;
  }

  const lock = {
    targetElement,
    options: options || {},
  };

  locks = [...locks, lock];

  setPositionFixed();

  if (
    !CSS.supports('overscroll-behavior', 'none') &&
    window.matchMedia('(pointer:coarse)').matches
  ) {
    targetElement.ontouchstart = (event: HandleScrollEvent) => {
      if (event.targetTouches.length === 1) {
        // detect single touch.
        initialClientY = event.targetTouches[0].clientY;
        initialClientX = event.targetTouches[0].clientX;
      }
    };
    targetElement.ontouchmove = (event: HandleScrollEvent) => {
      if (event.targetTouches.length === 1) {
        // detect single touch.
        handleScroll(event, targetElement);
      }
    };

    if (!documentListenerAdded) {
      document.addEventListener(
        'touchmove',
        preventDefault,
        hasPassiveEvents ? { passive: false } : undefined
      );
      documentListenerAdded = true;
    }
  }
};

type TRemoveEventType = (AddEventListenerOptions & EventListenerOptions) | undefined;

export const clearAllBodyScrollLocks = (): void => {
  // Clear all locks ontouchstart/ontouchmove handlers, and the references.
  locks.forEach((lock: Lock) => {
    lock.targetElement.ontouchstart = null;
    lock.targetElement.ontouchmove = null;
  });

  if (documentListenerAdded) {
    const opts: TRemoveEventType = hasPassiveEvents ? { passive: false } : undefined;
    document.removeEventListener('touchmove', preventDefault, opts);
    documentListenerAdded = false;
  }

  // Reset initial clientY.
  initialClientY = -1;
  initialClientX = -1;
  restorePositionSetting();

  locks = [];
};

export const enableBodyScroll = (targetElement: HTMLElement): void => {
  if (!targetElement) {
    // eslint-disable-next-line no-console
    console.error(
      'enableBodyScroll unsuccessful - targetElement must be provided when calling enableBodyScroll on IOS devices.'
    );
    return;
  }

  if (
    !CSS.supports('overscroll-behavior', 'none') &&
    window.matchMedia('(pointer:coarse)').matches
  ) {
    targetElement.ontouchstart = null;
    targetElement.ontouchmove = null;
  }

  locks = locks.filter((lock) => lock.targetElement !== targetElement);
  if (locks.length !== 0) {
    return;
  }
  clearAllBodyScrollLocks();
};

/**
 * body scroll lock 여부와 무관하게 body y 좌표를 설정하고 싶을 때 활용`
 */
export const forceSetBodyScrollY = (y: number) => {
  const targetY = Math.max(0, y);
  if (previousBodyPosition !== undefined) {
    // Body is locked (position: fixed); adjust the visual offset only.
    document.body.style.top = `${-targetY}px`;
  } else {
    // Not locked; perform a normal scroll.
    window.scrollTo(window.scrollX, targetY);
  }
};
